import React from "react";
import { AHText, Box, ListItem } from "arkh";
import { OrgSearchInput } from "../../OrgSearchInput";
import { graphql, useFragment } from "react-relay";
import { WorkspaceRoutineOccasionListHeader_RoutineWorkspaceOccasion$key } from "./__generated__/WorkspaceRoutineOccasionListHeader_RoutineWorkspaceOccasion.graphql";
import { FormattedMessage } from "react-intl";
import { SortableColumnTitle } from "../../SortableColumnTitle";
import { WorkspaceRoutineOccasionItemContainer } from "./WorkspaceRoutineOccasionItemContainer";
import { WorkspaceRoutineOccasionSelectButton } from "./WorkspaceRoutineOccasionSelectButton";
import { IssueBadge } from "../WorkspaceComplianceRoutines/IssueBadge";
import { PillIndicator } from "../../PillIndicator";

function SecondRow(props: { children: React.ReactNode }) {
  return (
    <Box
      spaceTop="small"
      style={{
        flexDirection: "row",
        height: 34,
        alignItems: "center",
      }}
    >
      {props.children}
    </Box>
  );
}

export type WorkspaceRoutineOccasionOrderByKey =
  | "store"
  | "totalIssueCount"
  | "activeIssueCount"
  | "status";

export function WorkspaceRoutineOccasionListHeader(props: {
  orderBy: WorkspaceRoutineOccasionOrderByKey | null;
  setOrderBy: (_: WorkspaceRoutineOccasionOrderByKey) => void;
  order: "ASC" | "DESC";
  setOrder: (_: "ASC" | "DESC") => void;
  search: string;
  setSearch: (search: string) => void;
  workspaceOccasion: WorkspaceRoutineOccasionListHeader_RoutineWorkspaceOccasion$key;
  isLast: boolean;
  openOccasionsHistory: () => void;
}) {
  const workspaceOccasion = useFragment(
    graphql`
      fragment WorkspaceRoutineOccasionListHeader_RoutineWorkspaceOccasion on RoutineWorkspaceOccasion {
        id
        storesCount
        storesCompletedCount
        issueCount
        registeredIssueCount

        ...WorkspaceRoutineOccasionSelectButton_workspaceRoutineOccasion
      }
    `,
    props.workspaceOccasion
  );

  const percentage =
    workspaceOccasion.storesCount > 0
      ? (workspaceOccasion.storesCompletedCount /
          workspaceOccasion.storesCount) *
        100
      : 0;

  return (
    <Box color="surface-background-light">
      <WorkspaceRoutineOccasionSelectButton
        openOccasionsHistory={props.openOccasionsHistory}
        routineWorkspaceOccasion={workspaceOccasion}
      />
      <OrgSearchInput value={props.search} onChangeText={props.setSearch} />
      <ListItem
        isLast={props.isLast}
        disabled
        border="border-primary"
        displayMode="TABLE"
      >
        <WorkspaceRoutineOccasionItemContainer
          storeName={
            <>
              <SortableColumnTitle
                onOrderBySelect={() => {
                  props.setOrderBy("store");
                }}
                onOrderChange={props.setOrder}
                order={props.orderBy === "store" ? props.order : null}
                title={
                  <FormattedMessage
                    defaultMessage="Store Name"
                    description="WorkspaceRoutineOccasionListHeader store name"
                  />
                }
              />
              <SecondRow>
                <AHText>
                  <FormattedMessage
                    defaultMessage="Total"
                    description="Workspace Routine Occasion List Header Total"
                  />
                </AHText>
              </SecondRow>
            </>
          }
          createdIssues={
            <>
              <SortableColumnTitle
                onOrderBySelect={() => {
                  props.setOrderBy("totalIssueCount");
                }}
                onOrderChange={props.setOrder}
                order={props.orderBy === "totalIssueCount" ? props.order : null}
                title={
                  <FormattedMessage
                    defaultMessage="Created Issues"
                    description="WorkspaceRoutineOccasionListHeader created issues"
                  />
                }
              />
              <SecondRow>
                {workspaceOccasion.registeredIssueCount > 0 ? (
                  <AHText>{workspaceOccasion.registeredIssueCount}</AHText>
                ) : null}
              </SecondRow>
            </>
          }
          activeIssues={
            <>
              <SortableColumnTitle
                onOrderBySelect={() => {
                  props.setOrderBy("activeIssueCount");
                }}
                onOrderChange={props.setOrder}
                order={
                  props.orderBy === "activeIssueCount" ? props.order : null
                }
                title={
                  <FormattedMessage
                    defaultMessage="Active Issues"
                    description="WorkspaceRoutineOccasionListHeader active issues"
                  />
                }
              />
              <SecondRow>
                <IssueBadge
                  count={workspaceOccasion.issueCount}
                  size="smallMedium"
                />
              </SecondRow>
            </>
          }
          status={
            <>
              <SortableColumnTitle
                onOrderBySelect={() => {
                  props.setOrderBy("status");
                }}
                onOrderChange={props.setOrder}
                order={props.orderBy === "status" ? props.order : null}
                title={
                  <FormattedMessage
                    defaultMessage="Status"
                    description="WorkspaceRoutineOccasionListHeader status"
                  />
                }
              />
              <SecondRow>
                <PillIndicator percentage={percentage} />
              </SecondRow>
            </>
          }
        />
      </ListItem>
    </Box>
  );
}
